import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Checkbox, Grid, Select } from 'semantic-ui-react';
import _ from 'lodash';

import { Filter, Main } from 'Containers';
import { createToast, toastType } from 'Common/ToastHelper';
import { LetterCategoryEnum } from 'Common/Constants/Enumeration/LetterCategoryEnum';
import { LanguageEnum } from 'Common/Constants/Enumeration/LanguageEnum';

import { getSiteSetup } from '../sitesetup/state/page/actions';
import { getTemplates } from './state/setup/actions';
import * as LetterSetupSelectors from './state/selectors';
import { TitledCard } from 'Components';

const LetterCategoryIdMap = _.invert(LetterCategoryEnum);

class LetterSetupContainer extends Component {
  state = {
    selectedLanguageId: 1,
    selectedCategoryId: null,
    selectedLetterId: null,
    selectedLetterCustomId: null,
    modifyExisting: false,
  };

  componentDidMount() {
    this.props.getTemplates();
    this.props.getSiteSetup();
  }

  getMainProps = () => {
    const { isReadOnly, isFetching } = this.props;
    return {
      toolbox: {
        rightButtons: [
          {
            loading: isFetching,
            content: 'Next',
            disabled: isFetching || isReadOnly,
            onClick: this.handleNext,
          },
        ],
        shown: true,
      },
    };
  };

  onLanguageSelect = (e, data) => {
    const selectedLanguageId = data.value;
    this.setState({ selectedLanguageId });
  };

  renderLanguageSelect = () => {
    const { isFetching } = this.props;
    const { selectedLanguageId } = this.state;
    const options = Object.entries(LanguageEnum).map(([text, value]) => ({
      value,
      text,
    }));
    return (
      <Select
        fluid
        options={options}
        onChange={this.onLanguageSelect}
        value={selectedLanguageId}
        disabled={isFetching}
        loading={isFetching}
      />
    );
  };

  onCategorySelect = (e, data) => {
    const selectedCategoryId = data.value;
    this.setState({
      selectedCategoryId,
      selectedLetterId: null,
      modifyExisting: false,
    });
  };

  renderCategorySelect = () => {
    const { selectedCategoryId } = this.state;
    const { isFetching, templates } = this.props;
    const categoryIds = templates.map(t => t.LetterCategoryId);
    const options = [...new Set(categoryIds)].map(LetterCategoryId => ({
      value: LetterCategoryId,
      text: LetterCategoryIdMap[LetterCategoryId]
        ? LetterCategoryIdMap[LetterCategoryId]
        : 'Unknown',
    }));

    return (
      <Select
        placeholder="Please select a category"
        fluid
        options={options}
        onChange={this.onCategorySelect}
        value={selectedCategoryId}
        disabled={isFetching}
        loading={isFetching}
      />
    );
  };

  onTemplateSelect = (e, data) => {
    const selectedLetterId = data.value;
    if (this.state.selectedLetterId !== selectedLetterId) {
      this.setState({
        selectedLetterId,
        selectedLetterCustomId: null,
        modifyExisting: false,
      });
    }
  };

  renderLetterSelect = () => {
    const { templates, isFetching } = this.props;
    const {
      selectedCategoryId,
      selectedLanguageId,
      selectedLetterId,
    } = this.state;

    const options = templates
      .filter(template => template.LetterCategoryId === selectedCategoryId)
      .filter(template => template.LanguageId === selectedLanguageId)
      .map(({ Id, Description }) => ({
        value: Id,
        text: Description,
      }));

    return (
      <Select
        fluid
        placeholder={
          options.length > 0
            ? 'Please select a letter template'
            : 'No letter templates available'
        }
        options={options}
        onChange={this.onTemplateSelect}
        value={selectedLetterId}
        disabled={isFetching || options.length < 1}
        loading={isFetching}
      />
    );
  };

  onLetterCustomSelect = (e, data) => {
    const selectedLetterCustomId = data.value;
    this.setState({ selectedLetterCustomId, modifyExisting: true });
  };

  renderSelectedLetterOptions = () => {
    const {
      selectedLetterId,
      modifyExisting,
      isFetching,
      selectedLetterCustomId,
    } = this.state;
    const { getTemplate, getTemplateLetterCustom, sites } = this.props;
    const template = getTemplate(selectedLetterId);
    const IsSiteSpecific = _.get(template, 'IsSiteSpecific', false);
    const customTemplates = getTemplateLetterCustom(selectedLetterId);
    const SitesMap = sites.reduce((acc, site, index) => {
      acc[site.Id] = index;
      return acc;
    }, {});
    const hasCustomTemplatesForSelectedLetter =
      customTemplates.filter(({ SiteId }) => {
        return template.IsSiteSpecific
          ? !_.isUndefined(SitesMap[SiteId])
          : true;
      }).length > 0;
    const showWarning = !modifyExisting && hasCustomTemplatesForSelectedLetter;

    const options = _(customTemplates)
      .filter(({ SiteId }) => !_.isUndefined(SitesMap[SiteId]))
      .map(({ Id, SiteId }) => ({
        value: Id,
        text: `${SiteId} ${_.get(sites, [SitesMap[SiteId], 'Description'])}`,
      }))
      .orderBy('text')
      .value();

    return (
      <React.Fragment>
        <Grid.Row columns={3}>
          <Grid.Column className="input-group--btn-group">
            {hasCustomTemplatesForSelectedLetter && (
              <Checkbox
                label="Modify Existing"
                checked={modifyExisting}
                onChange={(e, data) => {
                  const state = {
                    modifyExisting: data.checked,
                    selectedLetterCustomId: null,
                  };
                  /**
                   * For non site specific letters, the first customTemplates is their only custom letter
                   */
                  if (!IsSiteSpecific && data.checked) {
                    state.selectedLetterCustomId = _.get(customTemplates, [
                      '0',
                      'Id',
                    ]);
                  }
                  this.setState(state);
                }}
              />
            )}
          </Grid.Column>
        </Grid.Row>
        {showWarning && (
          <Grid.Row>
            <Grid.Column className="input-group--btn-group">
              <span className="text-red">
                There are existing custom letter templates, saved changes will
                overwrite the existing templates.
              </span>
            </Grid.Column>
          </Grid.Row>
        )}
        {IsSiteSpecific && hasCustomTemplatesForSelectedLetter && (
          <Grid.Row columns={3}>
            <Grid.Column className="input-group--btn-group">
              <label className="text-orange">Existing Letter(s)</label>
              <Select
                fluid
                placeholder={
                  options.length < 1
                    ? 'No Existing Custom Templates'
                    : 'Please select an existing letter'
                }
                options={options}
                onChange={this.onLetterCustomSelect}
                value={selectedLetterCustomId}
                disabled={isFetching || options.length < 1}
                loading={isFetching}
              />
            </Grid.Column>
          </Grid.Row>
        )}
      </React.Fragment>
    );
  };

  handleNext = () => {
    const { getTemplateLetterCustom, getTemplate, sites } = this.props;
    const {
      selectedLanguageId,
      selectedCategoryId,
      selectedLetterId,
      selectedLetterCustomId,
      modifyExisting,
    } = this.state;

    const template = getTemplate(selectedLetterId);
    const customTemplates = getTemplateLetterCustom(selectedLetterId);

    const SitesMap = sites.reduce((acc, site, index) => {
      acc[site.Id] = index;
      return acc;
    }, {});

    const hasCustomTemplatesForSelectedLetter =
      customTemplates.filter(({ SiteId }) => {
        return template.IsSiteSpecific
          ? !_.isUndefined(SitesMap[SiteId])
          : true;
      }).length > 0;

    if (!selectedCategoryId) {
      return createToast(
        toastType.error,
        'Error',
        'Please select a category',
        true
      );
    }
    if (!selectedLetterId) {
      return createToast(
        toastType.error,
        'Error',
        'Please select a template',
        true
      );
    }

    let url = `/lettersetup/edit/${selectedLetterId}?languageId=${selectedLanguageId}`;
    if (modifyExisting) {
      if (hasCustomTemplatesForSelectedLetter && !selectedLetterCustomId) {
        return createToast(
          toastType.error,
          'Error',
          'Please select an existing letter',
          true
        );
      }
      url += `&customId=${selectedLetterCustomId}`;
    }
    this.props.history.push(url);
  };

  render() {
    const { selectedLetterId } = this.state;
    /**
     * TODO: move to redux form, which would allow for saving the selected fields
     */
    return (
      <Fragment>
        <Filter title={'Letter Setup'} />
        <Main {...this.getMainProps()}>
          <div className="card-indent">
            <TitledCard>
              <Grid>
                <Grid.Row columns={3}>
                  <Grid.Column className="input-group--btn-group">
                    <label className="text-orange">Language</label>
                    {this.renderLanguageSelect()}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row columns={3}>
                  <Grid.Column className="input-group--btn-group">
                    <label className="text-orange">Category</label>
                    {this.renderCategorySelect()}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row columns={3}>
                  <Grid.Column className="input-group--btn-group">
                    <label className="text-orange">Letter Templates</label>
                    {this.renderLetterSelect()}
                  </Grid.Column>
                </Grid.Row>
                {Boolean(selectedLetterId) &&
                  this.renderSelectedLetterOptions()}
              </Grid>
            </TitledCard>
          </div>
        </Main>
      </Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  sites: _.get(state, 'SiteSetupState.page.siteSetup.Data'),
  getTemplate: templateId =>
    LetterSetupSelectors.getTemplate(state, templateId),
  templates: LetterSetupSelectors.getTemplates(state),
  getTemplateLetterCustom: templateId =>
    LetterSetupSelectors.getTemplateLetterCustom(state, templateId),
  isFetching: LetterSetupSelectors.getFetching(state),
});

const mapDispatchToProps = {
  getTemplates,
  getSiteSetup,
};

export const LetterSetup = withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(LetterSetupContainer)
);



// WEBPACK FOOTER //
// ./src/pages/lettersetup/LetterSetup.js